<template>
  <div class="free-chat-view">
    <div v-if="!dialogId" class="new-chat-container">
      <!-- Scrolling and centering are deliberately split across these two
           elements; see the .new-chat-container rule for why doing both on one
           element makes the top of this content unreachable. -->
      <div class="new-chat-centerer">
        <!-- The standing free-sample readout. Top-anchored above the branch
             content, so it is there before the sample runs out as well as
             after; hidden inside an open chat, where the fullscreen view owns
             the screen, and hidden when the wall below actually renders,
             because a "View plans" card directly above a wall of plans is
             redundant. `showLimitWall` IS the wall's render condition on this
             view, so the two cannot drift. -->
        <FreeTierUsagePill
          v-if="!showLimitWall"
          :user-data="studentInfo"
          :usage="lifetimeUsage"
          :sheet-open="plansSheetOpen"
          source="chat"
          @view-plans="openPlansSheet"
        />
        <!-- Everything that is not the card lives in one group so its auto
             block margins can do the centering the centerer used to do. -->
        <div class="new-chat-body">
        <!-- The wall is a statement about a RESOLVED verdict AND the absence of
             anything to resume. An unresolved verdict renders the welcome
             branch below with creation disabled, never this. -->
        <div v-if="showLimitWall" class="limit-reached">
          <!-- The sample-complete landing (spec 2026-08-03, render E): the free
               sample ending is an accomplishment with a next step, not a wall.
               Shown for every free-sample account; other accounts keep the legacy
               block below, whose copy is still true for a monthly allowance. -->
          <template v-if="showSampleComplete">
            <div class="sample-icon">
              <font-awesome-icon icon="check-circle" class="icon" aria-hidden="true" />
            </div>
            <h2>{{ $t('freeChat.sampleComplete.title') }}</h2>
            <ul class="sample-recap" role="list">
              <li class="recap-row">
                <font-awesome-icon icon="check" class="recap-icon recap-icon-done" aria-hidden="true" />
                <span>{{ $t('freeChat.sampleComplete.chatsDone') }}</span>
              </li>
              <li v-if="hasFreeLessonLeft" class="recap-row recap-row-waiting">
                <font-awesome-icon icon="gift" class="recap-icon recap-icon-waiting" aria-hidden="true" />
                <span>{{ $t('freeChat.sampleComplete.lessonWaiting') }}</span>
              </li>
              <li v-else class="recap-row">
                <font-awesome-icon icon="check" class="recap-icon recap-icon-done" aria-hidden="true" />
                <span>{{ $t('freeChat.sampleComplete.lessonDone') }}</span>
              </li>
            </ul>
            <p>{{ $t('freeChat.sampleComplete.body') }}</p>
            <!-- Offered ABOVE the plans on purpose: what is still free comes
                 before what costs money. -->
            <button
              v-if="hasFreeLessonLeft"
              type="button"
              class="button sample-complete-cta"
              @click="goToFreeLesson"
            >
              <font-awesome-icon icon="graduation-cap" class="button-icon" aria-hidden="true" />
              <span>{{ $t('freeChat.sampleComplete.goToLesson') }}</span>
            </button>
          </template>
          <template v-else>
            <div class="limit-icon">
              <font-awesome-icon icon="hourglass-end" class="icon" />
            </div>
            <h2>{{ $t('freeChat.limits.title') }}</h2>
            <!-- This sentence says "your free sample" out loud, so it is gated
                 on isFreeTierAccount (both free tests ANDed) rather than on the
                 billing-derived plan id alone. A comp'd account has no billing
                 record, so the plan id says "free" while the account holds a
                 monthly allowance the server honors; it takes the paid copy
                 below, which is the branch its ceiling actually came from. -->
            <p v-if="isFreeSampleAccount">
              {{ $t('freeChat.limits.freeSampleSpent') }}
            </p>
            <p v-else-if="currentPlanId === 'foundation'">
              {{ $t('freeChat.limits.foundationPlan') }}
            </p>
            <p v-else>
              {{ $t('freeChat.limits.fluencyPlan') }}
            </p>
          </template>

          <UpgradeOptions
            :current-plan-id="currentPlanId"
            :user-country="userCountry"
            :current-provider="currentProvider"
            :on-upgrade="handleUpgradeRequest"
            @iap-purchase-complete="handleIapPurchaseComplete"
          />
        </div>
        <div v-else class="welcome-container">
          <div class="empty-state-icon">
            <font-awesome-icon icon="comments" />
          </div>
          <h2 class="welcome-title">{{ $t('freeChat.welcome.title') }}</h2>
          <p class="welcome-subtitle">
            {{ $t('freeChat.welcome.subtitle') }}
          </p>
          <button
            class="button new-chat"
            :disabled="newChatDisabled"
            @click="newChat"
          >
            <font-awesome-icon icon="comments" class="button-icon" />
            <span>{{ $t('freeChat.welcome.button') }}</span>
          </button>
        </div>
        </div>
      </div>
    </div>

    <div v-else class="fullscreen-chat">
      <ChatScreen
        :dialogId="dialogId"
        @newChatRequest="newChat"
        class="chat-screen-container"
      />
    </div>

    <!-- The plans sheet the usage card raises. Deliberately mounted HERE, at
         the view root, and not next to the card inside v-if="!dialogId":
         dialogId is reassigned on every users/{uid} snapshot (including the one
         a completed purchase triggers), so a sheet in that subtree would be
         destroyed mid-flow. Same presentation as the lesson-limit sheet on My
         Course — the Modal supplies the surface, so UpgradeOptions goes in bare
         rather than stacking a second card inside the first. It is fed by the
         billing state this view already owns for the wall below, so the sheet
         and the wall can never quote different prices. -->
    <Modal
      v-model="plansSheetOpen"
      hide-footer
      :aria-label="$t('freeTierPill.viewPlans')"
      :dialog-class="'modal-dialog-centered modal-dialog-scrollable'"
    >
      <UpgradeOptions
        :current-plan-id="currentPlanId"
        :user-country="userCountry"
        :current-provider="currentProvider"
        :on-upgrade="handleUpgradeRequest"
        @iap-purchase-complete="handleIapPurchaseComplete"
      />
    </Modal>
  </div>
</template>

<script>
import ChatScreen from "@/components/ChatScreen.vue";
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { authStore } from "@/store/auth.js";
import { pricingStore } from "@/store/pricing.js";
import { stripeUtils } from "@/scripts/stripeUtils.js";
import { platformBilling } from "@/scripts/platformBilling.js";
import {
  getDatabase,
  ref as dbRef,
  push,
  set,
  onValue,
} from "firebase/database";
import Modal from "@/components/Modal.vue";
import UpgradeOptions from "@/components/UpgradeOptions.vue";
import FreeTierUsagePill from "@/components/FreeTierUsagePill.vue";
import {
  fetchUsageAndIncomplete,
  getStartOfMonthInTimezone,
  getEffectiveFreeLimits,
  isFreeTierAccount,
  isServerFreeUser,
  resolveMonthlyLimit,
  SERVER_DEFAULT_TIMEZONE,
} from "@/composables/useUsageCounts.js";
import { useRouter } from "vue-router";
import { Routes } from "@/constants/routes.js";
import { useToast } from "@/composables/useToast.js";
import { useUpsellEvents } from "@/composables/useUpsellEvents.js";
import i18n from "@/locales";
import { STORAGE_KEYS } from "@/constants/storageKeys.js";

// A freestyle chat starts when the client writes dialog-meta/{id}; the
// backend's saveNewDialog (which writes dialogs/{uid}/{lang}/{id}) runs ~10s
// later, after the LLM + TTS pipeline. During that window, dialogs/ has no
// entry for this chat, so a refresh / remount / back-nav would otherwise re-
// enable the button and create a duplicate. We cache the in-flight id locally
// so any remount returns into the same chat. A TTL bounds recovery if the
// backend never completes (LLM error, timeout) so the user isn't stuck forever.
const INFLIGHT_TTL_MS = 60_000;

export default {
  name: "FreeChatView",
  components: {
    ChatScreen,
    Modal,
    UpgradeOptions,
    FreeTierUsagePill,
  },

  setup() {
    const { showToast } = useToast();
    const upsellEvents = useUpsellEvents();
    const router = useRouter();
    const dialogId = ref(null);
    // Tracks which learning language the current dialogId belongs to. A
    // language switch (switcher / "Add and switch") fires watchStudentInfo with
    // a new selectedLanguage; without this, dialogId would keep pointing at the
    // previous language's chat and ChatScreen would keep rendering it.
    const dialogLang = ref(null);
    const newChatDisabled = ref(true);
    const studentInfo = ref(null);
    const hasReachedLimit = ref(false);
    const isProcessingUpgrade = ref(false);
    const currentPlanId = ref("free");
    const userCountry = ref("US");
    // Provider of the active subscription (null on free) so UpgradeOptions only
    // offers the payment path that provider supports.
    const currentProvider = ref(null);

    // The free lifetime ceiling this account is actually held to: the plan's
    // base value plus any well-formed users/{uid}/freebies grant. Derived from
    // the live studentInfo snapshot so a grant landing mid-session raises the
    // ceiling in place, and read from the shared helper so this matches
    // helpers/users.checkFreeUserLimit rather than re-deriving the node here.
    const effectiveChatLimit = computed(
      () => getEffectiveFreeLimits(studentInfo.value).chats
    );

    // The lifetime counts the free-tier verdict below was computed from, kept
    // so FreeTierUsagePill can show the same numbers this view is gating on
    // instead of running a second fan-read over dialogs/{uid}. Null for a paid
    // account, whose allowance is monthly and whose pill is hidden anyway.
    const lifetimeUsage = ref(null);

    // hasReachedLimit alone cannot say "we do not know yet", and false-because-
    // unresolved is indistinguishable from false-because-under-the-ceiling. The
    // wall must never render on a guess, so the verdict is tri-state: this flag
    // is set only by a limit resolution that actually landed, and cleared again
    // whenever the language being resolved changes underneath it.
    const limitResolved = ref(false);

    // The one render condition for the wall. `!dialogId` (the enclosing v-if)
    // is the "nothing to resume" half: a resumable session sets dialogId in the
    // same tick this verdict is published, from the same snapshot read, so
    // there is no interleaving in which the wall paints over an open session.
    const showLimitWall = computed(
      () => limitResolved.value && hasReachedLimit.value
    );

    // Whether the softer sample-complete landing replaces the legacy wall.
    //
    // `lifetimeUsage` doubles as the proof that the verdict on screen came from
    // the FREE branch of checkWeeklyLimit: it is only ever populated there. So
    // the recap below can never narrate lifetime counts next to a limit that
    // was actually computed monthly, and it cannot render before the counts
    // land. isFreeTierAccount is the same helper FreeTierUsagePill reads, so
    // the pill directly above and this block agree by construction.
    // The wall's copy branch. Same helper the sample-complete landing below
    // uses, so the two can never announce different accounts of the same
    // account: an account is on the free sample only when the features node is
    // absent AND the billing-derived plan is free.
    const isFreeSampleAccount = computed(() =>
      isFreeTierAccount(studentInfo.value)
    );

    const showSampleComplete = computed(
      () =>
        lifetimeUsage.value != null &&
        isFreeSampleAccount.value
    );

    // The other half of the sample. Freebies-aware through the same effective
    // ceiling the gate itself uses, so a granted account is told about the
    // lessons the server will actually let it start.
    const hasFreeLessonLeft = computed(
      () =>
        (lifetimeUsage.value?.lessons ?? 0) <
        getEffectiveFreeLimits(studentInfo.value).lessons
    );

    const goToFreeLesson = () => router.push(Routes.DASHBOARD_COURSE);

    // Discards a limit check whose snapshot has been superseded. The free/paid
    // branch is chosen BEFORE the awaits below, so two snapshots in quick
    // succession (a grant landing, a billing change, a language switch) leave
    // an older invocation in flight that would otherwise land its verdict --
    // and its usage counts -- on top of the newer one's. Every write below is
    // therefore gated on still owning the latest token.
    // LessonsView.checkPlanLimit carries the identical guard.
    let chatLimitToken = 0;


    // Resolves the limit verdict AND the resumable-session answer from ONE
    // dialogs/{uid} read (fetchUsageAndIncomplete). Two separate reads would
    // leave a drift window in which the wall can be painted over a user whose
    // session is still open — and on native WebViews, where this bug was
    // reported, that window is wide.
    //
    // Returns the payload for THIS invocation, or null when the invocation was
    // superseded. A failed read REJECTS rather than resolving to null, so a
    // user-initiated newChat() still surfaces errors.chat.startFailed the way
    // it always has; the passive resolution path catches it at the call site
    // and settles into the pending presentation. Both outcomes are treated as
    // an unknown verdict, which is fail-closed: no wall, and no creation.
    const checkWeeklyLimit = async () => {
      if (!studentInfo.value?.selectedLanguage) return null;

      const token = ++chatLimitToken;
      // The ONE snapshot this whole verdict is derived from, captured before
      // any await. Reading the branch from one source and the limit from a
      // second (this used to issue its own get() of users/{uid} inside the paid
      // branch) makes a torn verdict possible: a purchase or a comp landing
      // between the two reads produced a monthly count measured against a
      // ceiling from a different account state. `studentInfo` is the live
      // onValue snapshot, so it is never staler than a fresh one-shot read.
      const userData = studentInfo.value;

      // Which ceiling applies is the server's `helpers/users.isFreeUser` test
      // and nothing else. It used to be the billing-derived plan id, which an
      // admin comp breaks completely: a comp writes users/{uid}/features and no
      // billing record, so the plan id stays "free" and the user was held to
      // one lifetime chat while the backend was letting them start thirty.
      if (isServerFreeUser(userData)) {
        // Pooled across all languages, matching the server's lifetime count.
        const { usage, incomplete } = await fetchUsageAndIncomplete(
          authStore.userId,
          { type: "FREESTYLE_CHAT" }
        );
        if (token !== chatLimitToken) return null;
        lifetimeUsage.value = usage;
        hasReachedLimit.value = usage.chats >= effectiveChatLimit.value;
        limitResolved.value = true;
        return { atLimit: hasReachedLimit.value, incomplete };
      }

      // Paid and comp'd users: pooled across all languages, matching the
      // server. The month boundary comes from users/{uid}/timezone, which is
      // what `helpers/dialog.countDialogsThisMonth` applies server-side, and
      // falls back to UTC because `helpers/users.checkPaidUserLimit` reads
      // `userData.get("timezone", "UTC")`. This used to pass no zone at all,
      // i.e. the DEVICE zone, which put the client's boundary up to a day away
      // from the server's — enough to keep a comp'd user walled on the 1st of
      // the month while the backend had already reset their allowance.
      // LessonsView.checkPlanLimit computes its window the same way.
      const { usage, incomplete } = await fetchUsageAndIncomplete(
        authStore.userId,
        {
          sinceTimestamp: getStartOfMonthInTimezone(
            userData?.timezone || SERVER_DEFAULT_TIMEZONE
          ),
          type: "FREESTYLE_CHAT",
        }
      );
      const monthlyChatsCount = usage.chats;
      const monthlyLimit = resolveMonthlyLimit(userData, "chatsPerMonth");

      if (token !== chatLimitToken) return null;
      // Cleared here, after the awaits and under the token, rather than on the
      // way in: an early clear would blank the pill for the duration of a paid
      // read that a free snapshot may already have superseded.
      lifetimeUsage.value = null;
      // No resolvable limit is NOT a limit of zero and not the free lifetime
      // value either: `checkPaidUserLimit` returns False (do not block) when it
      // can resolve neither a features value nor a plan value, so the client
      // has to let the attempt through and let the server be the one to refuse
      // it. The old fallback to `plans.free.lifetimeChats` walled an entitled
      // account at one chat whenever its features node carried no
      // chatsPerMonth.
      hasReachedLimit.value =
        monthlyLimit !== null && monthlyChatsCount >= monthlyLimit;
      limitResolved.value = true;
      return { atLimit: hasReachedLimit.value, incomplete };
    };

    const handleUpgradeRequest = async (planType) => {
      isProcessingUpgrade.value = true;
      const [planName, billingCycleName] = planType.split("-");
      const plan = pricingStore.plans[planName];
      const lookupKey =
        billingCycleName === "month"
          ? plan.monthlyLookupKey
          : plan.yearlyLookupKey;

      try {
        await stripeUtils.startCheckout(lookupKey);
      } catch (error) {
        console.error("Error creating upgrade checkout session:", error);
        showToast(i18n.global.t("errors.billing.upgradeStartFailed"), "error");
      } finally {
        isProcessingUpgrade.value = false;
      }
    };

    const handleIapPurchaseComplete = () => {
      // Refresh user data to pick up new features
      hasReachedLimit.value = false;
      // The wall unmounts itself once hasReachedLimit clears, but the plans
      // sheet has its own flag and would otherwise sit over the chat the
      // purchase just unlocked until the reader dismissed it by hand. Native
      // IAP only reaches here after server-side entitlement verification, so
      // this is a completed purchase, not an optimistic one.
      plansSheetOpen.value = false;
      if (authStore.userId) {
        watchStudentInfo();
      }
    };

    // The plans sheet raised by the free-tier usage card. Mounted at the view
    // root (see the template comment) so a dialogId flip cannot destroy it.
    const plansSheetOpen = ref(false);

    const openPlansSheet = () => {
      plansSheetOpen.value = true;
      upsellEvents.trackSheetOpen({ source: "chat" });
    };

    // Stripe grants arrive by webhook while the sheet is open; the native path
    // closes through iap-purchase-complete above. Fluency is the ONLY plan
    // worth closing on, because it is the only one that leaves UpgradeOptions
    // with an empty body. A mid-session Foundation grant leaves the sheet up
    // showing the Fluency upsell, which is both renderable and correct.
    watch(currentPlanId, (planId) => {
      if (planId === "fluency") {
        plansSheetOpen.value = false;
      }
    });

    const readInflightChat = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEYS.FREE_CHAT_INFLIGHT);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (
          !parsed ||
          parsed.userId !== authStore.userId ||
          typeof parsed.createdAt !== "number" ||
          Date.now() - parsed.createdAt > INFLIGHT_TTL_MS
        ) {
          localStorage.removeItem(STORAGE_KEYS.FREE_CHAT_INFLIGHT);
          return null;
        }
        return parsed;
      } catch {
        return null;
      }
    };

    const writeInflightChat = (newDialogId, lang) => {
      try {
        localStorage.setItem(
          STORAGE_KEYS.FREE_CHAT_INFLIGHT,
          JSON.stringify({
            userId: authStore.userId,
            dialogId: newDialogId,
            lang,
            createdAt: Date.now(),
          })
        );
      } catch (error) {
        // Storage may be unavailable (Safari private mode, quota). Recovery on
        // remount is best-effort; the rest of the flow still works.
        console.warn("Failed to cache in-flight chat:", error);
      }
    };

    const clearInflightChat = () => {
      try {
        localStorage.removeItem(STORAGE_KEYS.FREE_CHAT_INFLIGHT);
      } catch {
        // ignore
      }
    };

    // Monotonic generation for the resume resolution, mirroring
    // chatLimitToken's discipline. A selectedLanguage string comparison alone
    // cannot see an A-B-A switch (French -> Spanish -> French): by the time the
    // first pass lands, the live language equals its own again, so it would
    // commit a two-generations-old chat over the newest one.
    let resumeToken = 0;

    // The latest incomplete freestyle chat in ONE language. Detection pools
    // every language (that is what the single read contains), but resume is
    // scoped to the selected language: a free account cannot switch languages,
    // so an incomplete row elsewhere is anomalous and must not drag the user
    // into a language they did not choose. Latest startedAt wins, because
    // "whichever key RTDB happened to iterate last" is an accident, not a rule.
    const latestIncompleteChatIn = (incomplete, lang) => {
      let latest = null;
      for (const session of incomplete) {
        if (session.lang !== lang) continue;
        if (session.type !== "FREESTYLE_CHAT") continue;
        if (!latest || (session.startedAt ?? 0) > (latest.startedAt ?? 0)) {
          latest = session;
        }
      }
      return latest;
    };

    const renderOngoingChatElseAllowNew = async () => {
      if (!studentInfo.value?.selectedLanguage) return;

      // Snapshot the language we're resolving. watchStudentInfo's onValue can
      // fire again (a rapid switch, or an optimistic write followed by its
      // confirm) while the awaits below are in-flight, starting a newer
      // invocation. If this now-stale one kept going it would overwrite
      // dialogId with the previous language's chat. We re-check the live
      // selectedLanguage after every await and bail the moment it diverges.
      const resolvingLang = studentInfo.value.selectedLanguage;
      const token = ++resumeToken;

      // If the active language changed since we last resolved a chat, the
      // current dialogId belongs to the previous language. Drop it now so the
      // stale chat clears immediately; the resolution below repopulates it for
      // the new language (or leaves the welcome screen if there's no ongoing
      // chat). Skip this on same-language re-fires (e.g. a billing update) to
      // avoid flashing the chat away and back.
      if (dialogLang.value !== resolvingLang) {
        dialogId.value = null;
        dialogLang.value = resolvingLang;
        // A verdict resolved against the previous language must not paint the
        // wall over this one while the new pass is still in flight.
        limitResolved.value = false;
        // Clearing dialogId exposes the welcome screen's "Start New Chat"
        // button. Disable it until the resolution below confirms the new
        // language's chat state — otherwise a tap during the async limit +
        // dialog lookup could call newChat() (which doesn't check for an
        // existing ongoing chat) and write a duplicate dialog-meta. The
        // terminal branches below re-enable it once resolution is done.
        newChatDisabled.value = true;
      }

      // ONE read answers both halves. The limit gates STARTING a chat, never
      // RESUMING one, so this no longer returns early at the ceiling — that
      // early return is the bug: it skipped the only code path that reopens an
      // in-progress free chat, and at a ceiling of 1 an in-progress chat always
      // reaches the ceiling by itself.
      let verdict = null;
      try {
        verdict = await checkWeeklyLimit();
      } catch (error) {
        // A failed read is an UNKNOWN verdict, not a permissive one. Leaving
        // limitResolved as it was keeps a previously resolved verdict on
        // screen; it never manufactures one. This path is passive (a snapshot
        // arrived), so there is nothing to toast — the fall-through below
        // simply keeps creation shut.
        console.error("Failed to resolve the chat limit:", error);
      }
      // A switch landed while checkWeeklyLimit was in-flight — a newer
      // invocation now owns the resolution; don't touch shared state.
      // (studentInfo.value may also have gone null on logout/reset mid-await.)
      if (token !== resumeToken) return;
      if (studentInfo.value?.selectedLanguage !== resolvingLang) return;
      if (!verdict) {
        // Unknown verdict (failed read, or superseded by a newer pass). Stay in
        // the pending presentation: no wall, and creation stays shut.
        newChatDisabled.value = true;
        return;
      }

      const resumable = latestIncompleteChatIn(verdict.incomplete, resolvingLang);
      if (resumable) {
        dialogId.value = resumable.dialogId;
        // The backend's saveNewDialog has caught up — any in-flight cache is
        // stale and can be dropped.
        clearInflightChat();
      } else if (!verdict.atLimit) {
        // dialogs/ has no in-progress freestyle for this user+lang. The chat
        // the user clicked might still be in the ~10s backend window; restore
        // the dialogId so this remount returns into the same chat instead of
        // offering the button again.
        //
        // Deliberately BEHIND the limit gate: at the ceiling the cache is not a
        // resume source. A concurrent second-device create the server refused
        // and deleted (tutor.py) would otherwise drop the user into a
        // ChatScreen for a dialog that no longer exists.
        const inflight = readInflightChat();
        if (inflight && inflight.lang === resolvingLang) {
          dialogId.value = inflight.dialogId;
        }
      }

      // Creation tracks the RESOLVED verdict, never the resume outcome:
      // reopening a session at the ceiling must not re-arm "Start New Chat",
      // because once that session completes the wall is the correct next state.
      // Below the ceiling this re-arms the button after a language switch —
      // when we resolved into an existing chat the welcome button stays hidden
      // (v-if="!dialogId"), so it only affects ChatScreen's newChatRequest.
      newChatDisabled.value = verdict.atLimit;
    };

    // See the guard at the top of newChat: this is the re-entrancy latch, not
    // the button's disabled state.
    let isCreatingChat = false;

    const newChat = async () => {
      if (!authStore.checkAuthOrElseRedirect()) return;
      // Re-entrancy guard, deliberately SEPARATE from the newChatDisabled UI
      // flag. Those two roles used to be one flag, which stopped being safe the
      // moment resuming at the ceiling became possible: newChatDisabled is
      // intentionally true while a resumed chat is open, and ChatScreen's
      // newChatRequest — what ChatFinishedStats emits when that chat completes —
      // must still reach the re-check below, which clears dialogId and hands the
      // user to the wall. Short-circuiting on the UI flag instead would strand
      // them on a finished chat with no way forward.
      if (isCreatingChat) return;

      // Guard against studentInfo being stale or partially loaded; writing
      // dialog-meta with lang: undefined would be rejected by Firebase and
      // an undefined lang in the localStorage cache would never match on
      // remount. The button should already be disabled in that state, but
      // a click racing with a studentInfo update could slip through.
      const lang = studentInfo.value?.selectedLanguage;
      if (!lang) return;

      isCreatingChat = true;
      newChatDisabled.value = true;

      try {
        // Fail-closed pre-write re-check. An unknown verdict is treated exactly
        // like at-limit: the server deletes a dialog it refuses, so a client
        // gate looser than the server's mints a hung chat. A read that FAILS
        // rejects instead, so this user-initiated action still reaches the
        // catch below and tells the user the start failed.
        const verdict = await checkWeeklyLimit();
        if (!verdict) return;
        if (verdict.atLimit) {
          dialogId.value = null;
          return;
        }

        const db = getDatabase();
        // note: push() creates a key client side only. It does not write to the database
        // until the next step. So, there's no race condition where the server sees the
        // path before the additional data is written.
        const dialogMetaRef = push(dbRef(db, "dialog-meta"));
        // Record the in-flight chat BEFORE writing dialog-meta. If anything
        // interrupts the page between this and the dialog-meta write, the
        // cached id will TTL out harmlessly. If we cached after, a crash
        // mid-write would leave a dialog-meta entry the user couldn't recover.
        writeInflightChat(dialogMetaRef.key, lang);
        set(dialogMetaRef, {
          userId: authStore.userId,
          lang,
          type: "FREESTYLE_CHAT",
          // Tap time (seconds): the true start of the tap-to-opener wait, before
          // the backend spends 15-32s on LLM+TTS. The server's startedAt is set
          // only after that work, so perceived-latency (tutor-openerWait) anchors
          // on this instead. See monitoring.recordPerceivedLatencyMetrics.
          clientStartedAt: Math.floor(Date.now() / 1000),
        });
        dialogId.value = dialogMetaRef.key;
        dialogLang.value = lang;
      } catch (error) {
        console.error("Failed to start new chat:", error);
        showToast(i18n.global.t("errors.chat.startFailed"), "error");
      } finally {
        isCreatingChat = false;
        // Track the latest RESOLVED verdict rather than unconditionally
        // re-arming: at the ceiling this attempt just sent the user to the
        // wall, and an unresolved verdict is not permission to try again.
        newChatDisabled.value = limitResolved.value
          ? hasReachedLimit.value
          : true;
      }
    };

    // Held outside watchStudentInfo so repeat calls replace the previous
    // subscription and onUnmounted can detach the live one. The old code
    // returned its cleanup from inside the Promise executor, where the return
    // value is discarded, so every call stacked another permanent listener
    // that also outlived the view (bug-hunt M3).
    let unsubscribeStudentInfo = null;
    // Resolver for the promise watchStudentInfo handed its caller. A detached
    // listener never fires, so settling it here is what stops a caller
    // awaiting a subscription that has already been replaced.
    let resolveFirstSnapshot = null;

    const settleFirstSnapshot = () => {
      if (resolveFirstSnapshot) {
        resolveFirstSnapshot();
        resolveFirstSnapshot = null;
      }
    };

    const detachStudentInfo = () => {
      if (unsubscribeStudentInfo) {
        unsubscribeStudentInfo();
        unsubscribeStudentInfo = null;
      }
      settleFirstSnapshot();
    };

    const watchStudentInfo = () => {
      const db = getDatabase();
      const userRef = dbRef(db, `users/${authStore.userId}`);

      detachStudentInfo();

      return new Promise((resolve) => {
        resolveFirstSnapshot = resolve;
        unsubscribeStudentInfo = onValue(userRef, (snapshot) => {
          const data = snapshot.val();
          studentInfo.value = data;
          // Determine current plan ID (respects billing.status, so a canceled
          // subscription correctly resolves to free)
          const plan = pricingStore.getCurrentPlan(data?.billing);
          currentPlanId.value = plan.id;
          // null when free; fall back to "stripe" for a paid plan whose legacy
          // billing row predates the provider field (matches AccountView).
          currentProvider.value =
            plan.id === "free" ? null : data?.billing?.provider || "stripe";
          userCountry.value = platformBilling.getUserCountry(data);

          if (data?.selectedLanguage) {
            // Keep the button disabled until renderOngoingChatElseAllowNew()
            // confirms there's no ongoing freestyle chat. On native WebViews
            // the dialogs query is slow enough that enabling the button here
            // lets the user spam-click before we've redirected them into the
            // ongoing chat.
            renderOngoingChatElseAllowNew();
          }

          // Resolve once: onValue re-fires on every change to the node.
          settleFirstSnapshot();
        },
        (error) => {
          // A cancelled subscription (permission denied after sign-out, a
          // stale token) never delivers a snapshot, so the success callback
          // above will not run. Settle here or the caller waits forever.
          console.error("Failed to read student info:", error);
          unsubscribeStudentInfo = null;
          settleFirstSnapshot();
        });
      });
    };

    onMounted(() => {
      if (authStore.userId) {
        watchStudentInfo();
      }
    });

    onUnmounted(detachStudentInfo);

    return {
      dialogId,
      newChatDisabled,
      hasReachedLimit,
      showLimitWall,
      isProcessingUpgrade,
      pricingStore,
      newChat,
      handleUpgradeRequest,
      handleIapPurchaseComplete,
      plansSheetOpen,
      openPlansSheet,
      // Exposed so its promise contract (settles on the first snapshot, or
      // when the subscription is replaced or torn down) is testable.
      watchStudentInfo,
      isFreeSampleAccount,
      effectiveChatLimit,
      currentPlanId,
      userCountry,
      currentProvider,
      studentInfo,
      lifetimeUsage,
      showSampleComplete,
      hasFreeLessonLeft,
      goToFreeLesson,
    };
  },
};
</script>

<style scoped>
.free-chat-view {
  height: 100%;
  min-height: 100%;
  /* LessonsView already paints this; here the page was the plain white
     --al-surface, so the raised white ledger card had no surface to lift off
     in light mode. Dark mode is unaffected (page and card are both remapped
     and already differ). */
  background: var(--al-surface-page);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  overflow: visible;
  position: relative;
  flex: 1 1 auto;
}

.fullscreen-chat {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  background-color: var(--al-surface);
  padding: 0.5rem;
  margin: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

@media (max-width: 767px) {
  .fullscreen-chat {
    padding: 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 49;
  }
}

@media (min-width: 768px) {
  .fullscreen-chat {
    padding: 1rem;
  }
}

.chat-screen-container {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
}

/* SCROLLS, and nothing on this element may ever center. A single element that
   both centers with justify-content and scrolls with overflow-y puts the
   overflow on BOTH sides of the centred content, and scrollTop cannot go below
   zero -- so as soon as the content is taller than the box, its top is
   permanently UNREACHABLE rather than merely scrolled off.
   That is not theoretical here: at 1440x900 the sample-complete screen overflows
   by ~35px and ate the whole usage pill, and at 375x667 it clips ~473px,
   burying the recap and the "start my free lesson" CTA so a phone shows nothing
   but the paid plans -- the exact inversion of "what is still free comes before
   what costs money".
   `justify-content: safe center` would also fix it, but only above Safari 15.4
   / Chrome 93; keeping the roles apart needs no browser floor, which matters
   for a Capacitor WKWebView pinned to an old iOS. Since the ledger-card
   redesign no element uses justify-content for this at all -- the centering
   lives in .new-chat-body's auto block margins, which are top-safe by
   construction (they collapse to zero on overflow). */
.new-chat-container {
  height: 100%;
  flex: 1 1 auto;
  /* The reserved slack is what this box gives back to the surrounding chrome.
     200px, not the old 150px, because the ledger card that replaced the usage
     pill is ~48px taller; leaving 150px here made the welcome branch overflow
     into a scrollbar on standard desktop viewports for a card that is supposed
     to cost nothing. */
  min-height: calc(100vh - 200px);
  min-height: calc(100dvh - 200px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* HOLDS THE COLUMN, does not scroll and no longer centers. min-height rather
   than height is what makes it safe: this element can grow past the scroller
   and scroll from its top with nothing clipped.

   `justify-content: center` used to live here, which centred the whole column
   -- card included -- so the card floated mid-screen on this tab while it sat
   top-anchored on My Course. Centering now belongs to .new-chat-body's auto
   block margins, which absorb the free space BELOW the card when content is
   short and resolve to zero when it overflows (CSS Flexbox 9.5). The safe
   split above is untouched; only which element centres changed. */
.new-chat-centerer {
  display: flex;
  /* Column, not the default row: the usage card is a second child of this
     container and a row would sit it BESIDE the welcome/limit panel and let it
     shrink. */
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  min-height: 100%;
  width: 100%;
}

/* Everything that is not the card. The auto block margins are the centering;
   the explicit width is not decoration -- the centerer's `align-items: center`
   would otherwise shrink this wrapper to fit-content, leaving .limit-reached's
   `width: 100%; max-width: 800px` to resolve against an accident of content
   width. */
.new-chat-body {
  margin-block: auto;
  width: 100%;
}

.button.new-chat {
  width: auto;
  min-width: 280px;
  padding: 1rem 2rem;
  font-size: 1.25rem;
  font-weight: 600;
  text-align: center;
  border: none;
  border-radius: var(--al-radius-md);
  color: var(--al-white);
  /* AA: white on --al-primary is ~3.3:1; primary-hover passes at ~4.67:1. */
  background: var(--al-primary-hover);
  box-shadow: 0 8px 24px color-mix(in srgb, var(--al-primary) 25%, transparent);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  outline: none;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.button-icon {
  font-size: 1.5rem;
}

.welcome-container {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.empty-state-icon {
  font-size: 48px;
  color: var(--al-light-blue);
  background: color-mix(in srgb, var(--al-primary) 8%, transparent);
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.welcome-title {
  font-size: 2.5rem;
  color: var(--al-primary);
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0;
}

.welcome-subtitle {
  font-size: 1.25rem;
  color: var(--al-text-muted);
  margin: 0;
  font-weight: 400;
}

/* Shimmer effect for button */
.button.new-chat::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in srgb, var(--al-white) 20%, transparent),
    transparent
  );
  transition: left 0.5s;
}

.button.new-chat:hover::before {
  left: 100%;
}

/* Subtle pulse animation */
@keyframes pulse {
  0% {
    box-shadow: 0 8px 24px color-mix(in srgb, var(--al-primary) 25%, transparent);
  }

  50% {
    box-shadow: 0 8px 24px color-mix(in srgb, var(--al-primary) 40%, transparent);
  }

  100% {
    box-shadow: 0 8px 24px color-mix(in srgb, var(--al-primary) 25%, transparent);
  }
}

.button.new-chat:not(:hover):not(:active):not(:disabled) {
  animation: pulse 3s ease-in-out infinite;
}

/* Mobile styles for the new chat button */
@media (max-width: 768px) {
  .button.new-chat {
    width: auto;
    min-width: 260px;
    padding: 1.25rem 2rem;
    font-size: 1.375rem;
    box-shadow: 0 10px 28px color-mix(in srgb, var(--al-primary) 30%, transparent);
  }

  .button-icon {
    font-size: 1.625rem;
  }
}

.button.new-chat:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px color-mix(in srgb, var(--al-primary) 35%, transparent);
}

.button.new-chat:active {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px color-mix(in srgb, var(--al-primary) 25%, transparent);
}

.button.new-chat:disabled {
  background-color: var(--al-gray);
  cursor: not-allowed;
  opacity: 0.5;
}

.limit-reached {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  /* Mobile overflow handling */
  width: 100%;
  box-sizing: border-box;
}

.limit-icon {
  font-size: 3rem;
  color: var(--al-orange);
  margin-bottom: 1rem;
}

.limit-reached h2 {
  font-size: 2rem;
  color: var(--al-text);
  margin-bottom: 1rem;
}

.limit-reached p {
  font-size: 1.1rem;
  color: var(--al-text-muted);
  margin-bottom: 2rem;
}

/* Sample-complete landing. DESIGN.md reserves the warm accent for celebration
   and upgrade emphasis, so the orb is the one warm element here; the free next
   step stays primary blue, and the paid CTAs belong to UpgradeOptions below. */
.sample-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1rem;
  border-radius: var(--al-radius-pill);
  background: var(--al-warm-soft);
  color: var(--al-orange);
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sample-recap {
  list-style: none;
  margin: 0 auto 1.5rem;
  padding: 0;
  display: inline-flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: left;
}

.recap-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  color: var(--al-text);
}

.recap-row-waiting {
  color: var(--al-text-muted);
}

.recap-icon {
  width: 1.25rem;
  flex: none;
}

.recap-icon-done {
  color: var(--al-success-dark);
}

.recap-icon-waiting {
  color: var(--al-orange);
}

.button.sample-complete-cta {
  width: auto;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 1rem;
  font-weight: 600;
  border: 1px solid var(--al-primary);
  border-radius: var(--al-radius-md);
  background: transparent;
  color: var(--al-primary-hover);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.button.sample-complete-cta:hover {
  background: var(--al-primary-soft);
}

.button.sample-complete-cta:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

.button.sample-complete-cta .button-icon {
  font-size: 1rem;
}

/* Mobile-specific adjustments */
@media (max-width: 768px) {
  /* `.new-chat-container.new-chat-container`, not a bare `.new-chat-container`:
     this rule exists to override the base rule at the top of this file, and a
     single class ties with it on specificity, which leaves the winner to
     whatever order the production CSS minifier happens to emit the two chunks
     in. That has silently broken release builds in this repo before, and here
     losing the race would restore the very reserve this rule removes.
     Repeating the class settles it in the cascade instead. */
  .new-chat-container.new-chat-container {
    /* No viewport reserve on mobile, and `min-height: 0` rather than nothing
       at all — the desktop `calc(100dvh - 200px)` above would otherwise
       cascade straight back in here.

       The constant was a guess at how much of the viewport the surrounding
       chrome takes. On mobile that chrome is `.content-area`'s own safe-area
       padding (top inset plus the 56px tab bar plus its inset), which varies
       by device, so any fixed number is wrong somewhere: guess low and this
       box is shorter than the room it has, guess high and it pushes
       `.free-chat-view` (whose `overflow` is `visible`) past `.content-area`'s
       content box, handing the shell scroll slack. Scroll into that slack and
       a tab switch clamps scrollTop to zero the moment both views go
       absolutely positioned, which reads as content hopping vertically.

       `min-height: 0` is also the declaration that lets `flex: 1 1 auto` do
       the sizing here, since the automatic minimum size of a column flex item
       is its min-content height, not zero. Asking the flex algorithm for the
       leftover space needs no constant and stays right on every device — the
       same replacement PR #799 made on LessonsView. */
    min-height: 0;
    padding: 1rem;
  }

  /* The auto block margins that used to live here are gone: they were what
     top-anchored the pill on mobile by absorbing the centerer's free space --
     i.e. mobile already shipped the layout the desktop restructure
     generalizes. Inside .new-chat-body they would resolve to zero by
     block-flow rules anyway, so keeping them would only be misleading. */
  .limit-reached {
    padding: 1rem;
  }

  .limit-icon {
    font-size: 2.5rem;
  }

  .limit-reached h2 {
    font-size: 1.5rem;
  }

  .limit-reached p {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }

  .welcome-title {
    font-size: 2rem;
  }

  .welcome-subtitle {
    font-size: 1.1rem;
    padding: 0 1rem;
  }
}
</style>
